import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DocRowNode } from '../../models/MaterialTable';
import { SelectionModel } from '@angular/cdk/collections';
import { DocRow } from '../libraries.component';
import { NgForOf, NgIf } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-tree-node',
  standalone: true,
  imports: [NgForOf, NgIf, MatCheckbox, MatIcon],
  template: `
    <div class="tree-node">
      <!-- Node Row -->
      <div *ngIf="node.expandable" [class]="'node-row level-' + node.level">
        <div class="node-row-content">
          <!-- Level 1: State column with checkbox -->
          <div class="doc-cell doc-cell-state" [style.width]="getColumnWidth('state')" *ngIf="node.level === 1">
            <mat-checkbox
              (click)="$event.stopPropagation()"
              (change)="onNodeSelectionToggle()"
              [checked]="isNodeSelected()"
              [indeterminate]="isNodePartiallySelected()"
              class="node-checkbox">
            </mat-checkbox>
            <div tabindex="0" (keyup.enter)="onToggleNode()" class="node-info" (click)="onToggleNode()">
              <mat-icon class="node-icon">{{ node.expanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
              <strong>{{ node.name }} ({{ getNodeCount() }})</strong>
            </div>
          </div>
          <div class="doc-cell doc-cell-state" [style.width]="getColumnWidth('state')" *ngIf="node.level !== 1"></div>

          <!-- Level 2: Utility column with checkbox -->
          <div class="doc-cell doc-cell-utility" [style.width]="getColumnWidth('utility')" *ngIf="node.level === 2">
            <mat-checkbox
              (click)="$event.stopPropagation()"
              (change)="onNodeSelectionToggle()"
              [checked]="isNodeSelected()"
              [indeterminate]="isNodePartiallySelected()"
              class="node-checkbox">
            </mat-checkbox>
            <div tabindex="0" (keyup.enter)="onToggleNode()" class="node-info" (click)="onToggleNode()">
              <mat-icon class="node-icon">{{ node.expanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
              <strong>{{ node.name }} ({{ getNodeCount() }})</strong>
            </div>
          </div>
          <div
            class="doc-cell doc-cell-utility"
            [style.width]="getColumnWidth('utility')"
            *ngIf="node.level !== 2"></div>

          <!-- Level 3: Proceeding column with checkbox -->
          <div
            class="doc-cell doc-cell-proceeding"
            [style.width]="getColumnWidth('proceeding')"
            *ngIf="node.level === 3">
            <mat-checkbox
              (click)="$event.stopPropagation()"
              (change)="onNodeSelectionToggle()"
              [checked]="isNodeSelected()"
              [indeterminate]="isNodePartiallySelected()"
              class="node-checkbox">
            </mat-checkbox>
            <div tabindex="0" (keyup.enter)="onToggleNode()" class="node-info" (click)="onToggleNode()">
              <mat-icon class="node-icon">{{ node.expanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
              <strong>{{ node.name }} ({{ getNodeCount() }})</strong>
            </div>
          </div>
          <div
            class="doc-cell doc-cell-proceeding"
            [style.width]="getColumnWidth('proceeding')"
            *ngIf="node.level !== 3"></div>

          <!-- Title column - empty for expandable nodes -->
          <div class="doc-cell doc-cell-title" [style.width]="getColumnWidth('title')"></div>
        </div>
      </div>

      <!-- Expanded Children Row -->
      <div *ngIf="node.expanded && node.expandable" class="node-children">
        <ng-container *ngFor="let child of node.children">
          <ng-container *ngIf="child.expandable">
            <!-- Recursive child node -->
            <app-tree-node
              [node]="child"
              [selection]="selection"
              [columnWidths]="columnWidths"
              (nodeToggle)="nodeToggle.emit($event)"
              (nodeSelectionToggle)="nodeSelectionToggle.emit($event)"
              (docSelectionToggle)="docSelectionToggle.emit($event)">
            </app-tree-node>
          </ng-container>
          <ng-container *ngIf="!child.expandable && child.doc">
            <!-- Document Row with aligned columns -->
            <div class="doc-row">
              <div class="doc-row-content">
                <div class="doc-cell doc-cell-state" [style.width]="getColumnWidth('state')"></div>
                <div class="doc-cell doc-cell-utility" [style.width]="getColumnWidth('utility')"></div>
                <div class="doc-cell doc-cell-proceeding" [style.width]="getColumnWidth('proceeding')"></div>
                <div class="doc-cell doc-cell-title" [style.width]="getColumnWidth('title')">
                  <mat-checkbox
                    (click)="$event.stopPropagation()"
                    (change)="onDocSelectionToggle(child.doc!)"
                    [checked]="isDocSelected(child.doc!)">
                  </mat-checkbox>
                  <span class="doc-title-text">{{ child.doc.title }}</span>
                </div>
              </div>
            </div>
          </ng-container>
        </ng-container>
      </div>
    </div>
  `,
  styleUrls: ['./tree-table.component.scss']
})
export class TreeNodeComponent {
  @Input() node!: DocRowNode;
  @Input() selection!: SelectionModel<DocRow>;
  @Input() columnWidths!: { state: number; utility: number; proceeding: number; title: number };

  @Output() nodeToggle = new EventEmitter<DocRowNode>();
  @Output() nodeSelectionToggle = new EventEmitter<DocRowNode>();
  @Output() docSelectionToggle = new EventEmitter<DocRow>();

  onToggleNode() {
    this.nodeToggle.emit(this.node);
  }

  onNodeSelectionToggle() {
    this.nodeSelectionToggle.emit(this.node);
  }

  onDocSelectionToggle(doc: DocRow) {
    this.docSelectionToggle.emit(doc);
  }

  getAllDocs(node: DocRowNode): DocRow[] {
    const docs: DocRow[] = [];
    if (node.doc) {
      docs.push(node.doc);
    }
    node.children.forEach(child => {
      docs.push(...this.getAllDocs(child));
    });
    return docs;
  }

  isNodeSelected(): boolean {
    const docs = this.getAllDocs(this.node);
    return docs.length > 0 && docs.every(doc => this.selection.isSelected(doc));
  }

  isNodePartiallySelected(): boolean {
    const docs = this.getAllDocs(this.node);
    const selectedCount = docs.filter(doc => this.selection.isSelected(doc)).length;
    return selectedCount > 0 && selectedCount < docs.length;
  }

  isDocSelected(doc: DocRow): boolean {
    return this.selection.isSelected(doc);
  }

  getNodeCount(): number {
    return this.getAllDocs(this.node).length;
  }

  getNodeLabel(): string {
    switch (this.node.level) {
      case 1:
        return `State: ${this.node.name}`;
      case 2:
        return `Company: ${this.node.name}`;
      case 3:
        return this.node.expandable ? `Proceeding: ${this.node.name}` : this.node.name;
      case 4:
        return this.node.name;
      default:
        return this.node.name;
    }
  }

  getColumnWidth(column: string): string {
    return `${this.columnWidths[column as keyof typeof this.columnWidths]}%`;
  }
}
